import {computed, onMounted, onUnmounted, ref} from "vue";
import {useMedicationCheckupStore} from "@/medication-checkup/stores/medication-checkup-store";
import {
    MedicationCheckupAction,
    MedicationCheckupBlock,
    MedicationCheckupSceneView
} from "@/medication-checkup/interfaces";
import {BEAT_MS, TYPING_MS} from "@/medication-checkup/constants";

/**
 * What a scene of the course is given: the served block, and whether it is
 * frozen — a frozen scene renders the record left behind, a live one
 * animates and submits.
 */
export interface SceneProps<TView extends MedicationCheckupSceneView> {
    block: MedicationCheckupBlock<TView>;
    frozen: boolean;
}

/**
 * How a scene wants its landings paced, and what it sets off once they are
 * armed.
 */
export interface SceneStaging {
    /**
     * Whether a card lands one beat after the bubble — what most scenes do.
     * A scene pacing its own landings — the ones running a scan, revealing
     * their matter step by step — leaves it off and arms its own with
     * `later`.
     */
    card?: boolean;
    /**
     * What a live scene sets off once its landings are armed: ask the server
     * for a draft, run a scan. Never called on a frozen scene.
     */
    live?: () => void;
    /**
     * What a frozen scene shows at once, beyond the bubble and the card: the
     * steps a live scene would have revealed one by one.
     */
    frozen?: () => void;
}

/**
 * What every scene of the course shares: the pacing of its landings, the
 * served view it renders from, and the one act that submits its block.
 *
 * Each block keeps its own scene — its card, its matter, its place in the
 * course — and they all play the same opening: the Copilote types, the
 * bubble lands, a card follows a beat later, the click echoes as the
 * pharmacist's word. That much is held here, so a scene is left with what is
 * its own.
 *
 * Nothing composed here is text: everything the scene shows comes from the
 * view its handler serves.
 */
export const useScene = <TView extends MedicationCheckupSceneView>(
    props: SceneProps<TView>,
    staging: SceneStaging = {}
) => {
    const store = useMedicationCheckupStore();

    /**
     * Whether the bubble has landed — the Copilote types until it has.
     */
    const bubbleShown = ref(false);

    /**
     * Whether the card of the scene has landed, a beat after the bubble.
     */
    const cardShown = ref(false);

    /**
     * The action clicked, held while its submission is in flight.
     */
    const clicked = ref<MedicationCheckupAction | null>(null);

    /**
     * Pending animation timers, cancelled on unmount so none of them lands
     * on a scene that is gone.
     */
    const timers: number[] = [];

    /**
     * Arm a landing. The timer is answered, so a scene cancelling a reveal
     * of its own can keep hold of the ones it must call off on a retry.
     */
    const later = (callback: () => void, delay: number): number => {
        const id = window.setTimeout(callback, delay);

        timers.push(id);

        return id;
    };

    onUnmounted(() => timers.splice(0).forEach(id => window.clearTimeout(id)));

    /**
     * The pacing of the live scene. A frozen scene animates nothing: the
     * record renders whole, instantly.
     */
    onMounted(() => {
        if (props.frozen) {
            bubbleShown.value = true;

            if (staging.card) {
                cardShown.value = true;
            }

            staging.frozen?.();

            return;
        }

        later(() => bubbleShown.value = true, TYPING_MS);

        if (staging.card) {
            later(() => cardShown.value = true, TYPING_MS + BEAT_MS);
        }

        staging.live?.();
    });

    const view = computed(() => props.block.view);

    const text = computed(() => view.value?.text ?? '');

    const actions = computed(() => view.value?.actions ?? []);

    /**
     * The line carrying the course on to what follows, served once the block
     * is validated — nothing for a scene closing on no such line.
     */
    const closing = computed(() => view.value?.closing ?? null);

    /**
     * The action the scene was closed with, served frozen so the echo can
     * replay it.
     */
    const acted = computed(() => view.value?.acted ?? null);

    /**
     * Submit the block on an action, with whatever the scene answers
     * alongside it.
     *
     * The echo lands with the click rather than with the answer — the act is
     * the pharmacist's, not the network's — and comes back if the submission
     * fails, so the action can be retried; the store has already reported
     * why.
     */
    const act = async (
        action: MedicationCheckupAction,
        payload: Record<string, unknown> = {}
    ): Promise<void> => {
        clicked.value = action;

        try {
            await store.submitBlock(props.block.id, {action: action.key, ...payload});
        } finally {
            clicked.value = null;
        }
    };

    return {bubbleShown, cardShown, clicked, later, view, text, actions, closing, acted, act};
};
